/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component, ViewChild, Input, OnDestroy, HostListener, OnInit,
} from '@angular/core';
import { Popover } from 'primeng/popover';
import { Subscription } from 'rxjs';
// @ts-ignore
import { fairyDustCursor } from '../../../cursor-effect/fairyDust.js';
import { ShareStateStore, SharePanelOverlays } from '../share-state.store';
import { WindowScrollingService } from '../window-scrolling.service';
import { AppConfigService } from '../../../common/services/app-config/app-config.service';

@Component({
  selector: 'bulb-share-panel',
  templateUrl: './share-panel.component.html',
  styleUrls: ['./share-panel.component.scss'],
  standalone: false,
})
export class SharePanelComponent implements OnDestroy, OnInit {
  public sharePanelOpen: boolean = false;

  public inviteModalOpen: boolean = false;

  private multiSelectPanelOpen: boolean = false;

  private multiSelectPanelSubscription: Subscription;

  @Input() disableScroll: boolean = false;

  @Input() compositeUnit: boolean;

  @Input() unitId: string;

  @Input() parentUnitId: string;

  @ViewChild('popover') popover: Popover;

  constructor(
    private appConfig: AppConfigService,
    private shareStateStore: ShareStateStore,
    private windowScroll: WindowScrollingService,
  ) { }

  ngOnInit() {
    this.multiSelectPanelSubscription = this.shareStateStore.state$.subscribe((state) => {
      this.multiSelectPanelOpen = state.multiSelectPanelOpen;
      this.inviteModalOpen = state.inviteModalOpen;
      this.sharePanelOpen = state.sharePanelOpen;

      if (state.sharePanelOpen && this.disableScroll) {
        this.windowScroll.disable();
      } else {
        this.windowScroll.enable();
      }
    });
  }

  /**
   * As of PrimeNG v12, the Popover component's [dismissable] input does not update with changes.
   * 'dismissable' means, when the user clicks outside of the Popover, the Popover will close.
   * We want the panel to be dismissable when it first opens, but not dismissable when any subsequent modals are opened.
   *
   * So, we set the dismissable property to false to avoid Popover's default dismissable behavior.
   * Then add an event listener to the window to detect when the user clicks outside of the Popover.
   * If the click event target matches a child of the Popover or a PrimeNG dialog,
   * then the Popover will not close.
   * Otherwise, the Popover will close.
   */
  @HostListener('window:click', ['$event'])
  handleClick(event: { target: Element; }): void {
    // If the overlay isn't open, or it is not currently dismissable, return early.
    if (!this.sharePanelOpen || this.inviteModalOpen) return;

    const { target } = event;
    const targetIsPopover: boolean = target.matches('.p-popover, .p-popover *');
    const targetIsDialog: boolean = target.matches('.p-dialog-mask, .p-dialog-mask *');

    if (!targetIsPopover && !targetIsDialog) {
      this.hide();
    }
  }

  /**
   * As of PrimeNG v12, the Popover component closes automatically on the resize event.
   * This method is a workaround to prevent the Popover from closing when sub-modals are open.
   *
   * On Android phones, opening the keyboard will trigger a resize event.
   * The MultiSelect component is a child of InviteFormComponent, which is a child of SharePanelComponent.
   * When the MultiSelect component's dropdown panel is open, the search input will be focused,
   * displaying the keyboard and triggering a resize event.
   * Canceling the resize event when the MultiSelect's panel is open will prevent the SharePanelComponent from closing.
   * This event listener must be created before the InviteFormComponent to ensure its event listener is called before
   * PrimeNG's popover event listener.
   */
  @HostListener('window:resize', ['$event'])
  stopResize(e: Event): void {
    if (this.multiSelectPanelOpen) {
      e.stopImmediatePropagation();
    }
  }

  public toggle($event: Event): void {
    if (this.sharePanelOpen) {
      this.hide();
    } else {
      this.open($event);
    }
  }

  private open(event: Event): void {
    // PrimeNG's Popover requires the event passed in.
    this.popover.show(event);
    event.stopPropagation();
    this.shareStateStore.showPanel(SharePanelOverlays.SHARE_PANEL);

    // Resets the state store value when the panel is opened.
    this.shareStateStore.hasStudentShared = false;
  }

  public hide(): void {
    this.popover.hide();
    this.shareStateStore.hidePanel(SharePanelOverlays.SHARE_PANEL);
    this.cursorEffect();
  }

  /**
   * Shows a fairy dust cursor effect after a student shares a page for the first time.
   * @private
   */
  private cursorEffect(): void {
    if (!this.shareStateStore.hasStudentShared) return;

    // eslint-disable-next-line new-cap,no-new
    new fairyDustCursor({
      colors: ['#ed1847', '#f26522', '#faa61a', '#fff200', '#00bfdf', '#00aeef'],
      fadeOutLength: this.appConfig.config.cursorEffect.length,
    });
  }

  ngOnDestroy(): void {
    this.shareStateStore.hidePanel(SharePanelOverlays.SHARE_PANEL);
    this.multiSelectPanelSubscription.unsubscribe();
  }
}
